import React, { createContext, useState, ReactNode, useEffect } from 'react';
import { MyProjectsContextType } from './types/myProjectsContextType';
import { EProjectStatus, IProject } from '../data_models/project';
import useCurrentUser from '../hooks/useCurrentUser';
import { EUserType } from '../data_models/enums';
import useProjects from '../hooks/useProjects';
import { IFreelancer, RestrictedFreelancerRole } from '../data_models/freelancer';
import { IEmployer } from '../data_models/employer';
import { sortByCreatedAt } from '../utils/projectUtils';


// Create the context
export const MyProjectsContext = createContext<MyProjectsContextType | undefined>(undefined);

interface MyProjectsProviderProps {
    children: ReactNode;
}

// Provider component
export const MyProjectsProvider: React.FC<MyProjectsProviderProps> = ({ children }) => {

    // public
    const [projectsAsFreelancerInProgress, setProjectsAsFreelancerInProgress] = useState<IProject[]>([]);
    const [projectsAsFreelancerCompleted, setProjectsAsFreelancerCompleted] = useState<IProject[]>([]);

    const [projectsAsPmWaitingForApproval, setProjectsAsPmWaitingForApproval] = useState<IProject[]>([]);
    const [projectsAsPmWaitingForCompletion, setProjectsAsPmWaitingForCompletion] = useState<IProject[]>([]);
    const [projectsAsPmOngoing, setProjectsAsPmOngoing] = useState<IProject[]>([]);
    const [projectsAsPmCompleted, setProjectsAsPmCompleted] = useState<IProject[]>([]);

    const [projectsAsEmployerDraft, setProjectsAsEmployerDraft] = useState<IProject[]>([]);
    const [projectsAsEmployerNotStarted, setProjectsAsEmployerNotStarted] = useState<IProject[]>([]);
    const [projectsAsEmployerWaitingForApproval, setProjectsAsEmployerWaitingForApproval] = useState<IProject[]>([]);
    const [projectsAsEmployerOngoing, setProjectsAsEmployerOngoing] = useState<IProject[]>([]);
    const [projectsAsEmployerCompleted, setProjectsAsEmployerCompleted] = useState<IProject[]>([]);
    const [projectsAsEmployerWaitingForCompletion, setProjectsAsEmployerWaitingForCompletion] = useState<IProject[]>([]);

    const [projectIdTeamMap, setProjectIdTeamMap] = useState<{ [key: string]: IFreelancer[] }>({}); // map project id -> team
    const [projectIdEmployerMap, setProjectIdEmployerMap] = useState<{ [key: string]: IEmployer }>({}); // map project id -> employer
    const [projectIdPmMap, setProjectIdPmMap] = useState<{ [key: string]: IFreelancer }>({}); // map project id -> project manager

    // pagination
    const [projectsAsFreelancerPage, setProjectsAsFreelancerPage] = useState(1);
    const [projectsAsFreelancerLimit, setProjectsAsFreelancerLimit] = useState(10);
    const [projectsAsPmPage, setProjectsAsPmPage] = useState(1);
    const [projectsAsPmLimit, setProjectsAsPmLimit] = useState(10);
    const [projectsAsEmployerPage, setProjectsAsEmployerPage] = useState(1);
    const [projectsAsEmployerLimit, setProjectsAsEmployerLimit] = useState(10);


    const { currentUser, currentUserType } = useCurrentUser();
    const {
        fetchProjectsByFilter,
        createProjectDraft: _createProjectDraft,
        updateProjectDetails: _updateProjectDetails,
        deleteProjectDraft: _deleteProjectDraft,
        publishProject: _publishProject,
        approveProjectDetails: _approveProjectDetails,
        requestProjectApproval: _requestProjectApproval,
        rejectApprovalRequest: _rejectApprovalRequest,
        requestProjectCompletion: _requestProjectCompletion,
        rejectCompletionRequest: _rejectCompletionRequest,
        completeProject: _completeProject,
    } = useProjects();

    useEffect(() => {
        try {
            if (currentUser && currentUserType) {
                if (currentUserType === EUserType.FREELANCER) {
                    fetchProjectsAsFreelancer();
                    if ((currentUser as IFreelancer).restrictedRoles?.includes(RestrictedFreelancerRole.PROJECT_MANAGER)) {
                        fetchProjectsAsPm();
                    }
                } else if (currentUserType === EUserType.EMPLOYER) {
                    fetchProjectsAsEmployer();
                }
            }
        } catch (error) {
            console.error('MyProjectsProvider error:', error);
        }

    }, [currentUser, currentUserType]);

    const fetchProjectsAsFreelancer = async () => {

        if (!currentUser || currentUserType !== EUserType.FREELANCER) return;

        const filter = {
            assignedFreelancers: { $elemMatch: { freelancerId: currentUser._id } }
        }
        const results = await fetchProjectsByFilter(
            filter,
            projectsAsFreelancerPage,
            projectsAsFreelancerLimit
        );

        const newProjectsAsFreelancerInProgress = [];
        const newProjectsAsFreelancerCompleted = [];

        const newProjectIdTeamMap: { [key: string]: IFreelancer[] } = { ...projectIdTeamMap };
        const newProjectIdEmployerMap: { [key: string]: IEmployer } = { ...projectIdEmployerMap };
        const newProjectIdPmMap: { [key: string]: IFreelancer } = { ...projectIdPmMap };


        for (const res of results) {
            const project = res.project;
            const team = res.team;
            const employer = res.employerBrief;
            const pm = res.pmBrief;

            newProjectIdTeamMap[project._id!] = team;
            newProjectIdEmployerMap[project._id!] = employer;
            newProjectIdPmMap[project._id!] = pm;

            switch (project.status) {
                case EProjectStatus.IN_PROGRESS:
                    newProjectsAsFreelancerInProgress.push(project);
                    break;
                case EProjectStatus.IN_PROGRESS_REASSIGN_PM:
                    newProjectsAsFreelancerInProgress.push(project);
                    break;
                case EProjectStatus.COMPLETED:
                    newProjectsAsFreelancerCompleted.push(project);
                    break;
                default:
                    break;
            }
        }

        setProjectIdTeamMap((prev) => ({
            ...prev,
            ...newProjectIdTeamMap,
        }));
        setProjectIdEmployerMap((prev) => ({
            ...prev,
            ...newProjectIdEmployerMap,
        }));
        setProjectIdPmMap((prev) => ({
            ...prev,
            ...newProjectIdPmMap,
        }));

        setProjectsAsFreelancerInProgress(newProjectsAsFreelancerInProgress);
        setProjectsAsFreelancerCompleted(newProjectsAsFreelancerCompleted);
    }

    const fetchProjectsAsPm = async () => {
        if (!currentUser || currentUserType !== EUserType.FREELANCER || !currentUser._emailVerified) return;

        const filter = {
            currentProjectManager: currentUser._id,
        }
        const results = await fetchProjectsByFilter(
            filter,
            projectsAsPmPage,
            projectsAsPmLimit
        );

        const newProjectsAsPmWaitingForApproval = [];
        const newProjectsAsPmOngoing = [];
        const newProjectsAsPmWaitingForCompletion = [];
        const newProjectsAsPmCompleted = [];

        const newProjectIdTeamMap: { [key: string]: IFreelancer[] } = { ...projectIdTeamMap };
        const newProjectIdEmployerMap: { [key: string]: IEmployer } = { ...projectIdEmployerMap };
        const newProjectIdPmMap: { [key: string]: IFreelancer } = { ...projectIdPmMap };

        for (const res of results) {
            const project = res.project;
            const team = res.team;
            const employer = res.employerBrief;
            const pm = res.pmBrief;

            newProjectIdTeamMap[project._id!] = team;
            newProjectIdEmployerMap[project._id!] = employer;
            newProjectIdPmMap[project._id!] = pm;

            switch (project.status) {
                case EProjectStatus.NOT_STARTED:
                    newProjectsAsPmOngoing.push(project);
                    break;
                case EProjectStatus.NOT_STARTED_APPROVAL_REQUESTED:
                    newProjectsAsPmWaitingForApproval.push(project);
                    break;
                case EProjectStatus.NOT_STARTED_APPROVAL_REJECTED:
                    newProjectsAsPmOngoing.push(project);
                    break;
                case EProjectStatus.IN_PROGRESS:
                    newProjectsAsPmOngoing.push(project);
                    break;
                case EProjectStatus.COMPLETION_REQUESTED:
                    newProjectsAsPmWaitingForCompletion.push(project);
                    break;
                case EProjectStatus.COMPLETION_REQUEST_REJECTED:
                    newProjectsAsPmOngoing.push(project);
                    break;
                case EProjectStatus.COMPLETED:
                    newProjectsAsPmCompleted.push(project);
                    break;
                default:
                    break;
            }
        }

        setProjectsAsPmWaitingForApproval(newProjectsAsPmWaitingForApproval);
        setProjectsAsPmOngoing(newProjectsAsPmOngoing);
        setProjectsAsPmWaitingForCompletion(newProjectsAsPmWaitingForCompletion);
        setProjectsAsPmCompleted(newProjectsAsPmCompleted);

        setProjectIdTeamMap((prev) => ({
            ...prev,
            ...newProjectIdTeamMap,
        }));
        setProjectIdEmployerMap((prev) => ({
            ...prev,
            ...newProjectIdEmployerMap,
        }));
        setProjectIdPmMap((prev) => ({
            ...prev,
            ...newProjectIdPmMap,
        }));
    }

    const fetchProjectsAsEmployer = async () => {
        if (!currentUser || currentUserType !== EUserType.EMPLOYER || !currentUser._emailVerified) return;
        const filter = {
            employerId: currentUser._id,
        }
        const results = await fetchProjectsByFilter(
            filter,
            projectsAsEmployerPage,
            projectsAsEmployerLimit
        );

        const newProjectsAsEmployerDraft = [];
        const newProjectsAsEmployerNotStarted = [];
        const newProjectsAsEmployerWaitingForApproval = [];
        const newProjectsAsEmployerOngoing = [];
        const newProjectsAsEmployerWaitingForCompletion = [];
        const newProjectsAsEmployerCompleted = [];

        const newProjectIdTeamMap: { [key: string]: IFreelancer[] } = {};
        const newProjectIdEmployerMap: { [key: string]: IEmployer } = {};
        const newProjectIdPmMap: { [key: string]: IFreelancer } = {};

        for (const res of results) {
            const project = res.project;
            const team = res.team;
            const employer = res.employerBrief;
            const pm = res.pmBrief;

            newProjectIdTeamMap[project._id!] = team;
            newProjectIdEmployerMap[project._id!] = employer;
            newProjectIdPmMap[project._id!] = pm;

            switch (project.status) {
                case EProjectStatus.DRAFT:
                    newProjectsAsEmployerDraft.push(project);
                    break;
                case EProjectStatus.WAITING_FOR_PM:
                    newProjectsAsEmployerNotStarted.push(project);
                    break;
                case EProjectStatus.NOT_STARTED:
                    newProjectsAsEmployerNotStarted.push(project);
                    break;
                case EProjectStatus.NOT_STARTED_APPROVAL_REQUESTED:
                    newProjectsAsEmployerWaitingForApproval.push(project);
                    break;
                case EProjectStatus.NOT_STARTED_APPROVAL_REJECTED:
                    newProjectsAsEmployerNotStarted.push(project);
                    break;
                case EProjectStatus.IN_PROGRESS_REASSIGN_PM:
                    newProjectsAsEmployerOngoing.push(project);
                    break;
                case EProjectStatus.IN_PROGRESS:
                    newProjectsAsEmployerOngoing.push(project);
                    break;
                case EProjectStatus.COMPLETION_REQUESTED:
                    newProjectsAsEmployerWaitingForCompletion.push(project);
                    break;
                case EProjectStatus.COMPLETION_REQUEST_REJECTED:
                    newProjectsAsEmployerOngoing.push(project);
                    break;
                case EProjectStatus.COMPLETED:
                    newProjectsAsEmployerCompleted.push(project);
                    break;
                default:
                    break;
            }
        }
        setProjectIdTeamMap((prev) => ({
            ...prev,
            ...newProjectIdTeamMap,
        }));
        setProjectIdEmployerMap((prev) => ({
            ...prev,
            ...newProjectIdEmployerMap,
        }));
        setProjectIdPmMap((prev) => ({
            ...prev,
            ...newProjectIdPmMap,
        }));

        setProjectsAsEmployerDraft(newProjectsAsEmployerDraft);
        setProjectsAsEmployerNotStarted(newProjectsAsEmployerNotStarted);
        setProjectsAsEmployerWaitingForApproval(newProjectsAsEmployerWaitingForApproval);
        setProjectsAsEmployerOngoing(newProjectsAsEmployerOngoing);
        setProjectsAsEmployerWaitingForCompletion(newProjectsAsEmployerWaitingForCompletion);
        setProjectsAsEmployerCompleted(newProjectsAsEmployerCompleted);
    }

    const refetchProjectById = async (projectId: string) => {
        const result = await fetchProjectsByFilter(
            { _id: projectId },
            1,
            9999
        );
        const project = result[0].project;
        // find project by id in local data
        // freelancer
        if (projectsAsFreelancerInProgress.find((p) => p._id === projectId)) {
            const newProjectsAsFreelancerInProgress = projectsAsFreelancerInProgress.map((p) => p._id === projectId ? project : p);
            setProjectsAsFreelancerInProgress(newProjectsAsFreelancerInProgress);
        } else if (projectsAsFreelancerCompleted.find((p) => p._id === projectId)) {
            const newProjectsAsFreelancerCompleted = projectsAsFreelancerCompleted.map((p) => p._id === projectId ? project : p);
            setProjectsAsPmOngoing(newProjectsAsFreelancerCompleted);
        }

        // pm
        else if (projectsAsPmOngoing.find((p) => p._id === projectId)) {
            const newProjectsAsPmOngoing = projectsAsPmOngoing.map((p) => p._id === projectId ? project : p);
            setProjectsAsPmOngoing(newProjectsAsPmOngoing);
        } else if (projectsAsPmWaitingForApproval.find((p) => p._id === projectId)) {
            const newProjectsAsPmWaitingForApproval = projectsAsPmWaitingForApproval.map((p) => p._id === projectId ? project : p);
            setProjectsAsPmWaitingForApproval(newProjectsAsPmWaitingForApproval);
        } else if (projectsAsPmWaitingForCompletion.find((p) => p._id === projectId)) {
            const newProjectsAsPmWaitingForCompletion = projectsAsPmWaitingForCompletion.map((p) => p._id === projectId ? project : p);
            setProjectsAsPmWaitingForCompletion(newProjectsAsPmWaitingForCompletion);
        } else if (projectsAsPmCompleted.find((p) => p._id === projectId)) {
            const newProjectsAsPmCompleted = projectsAsPmCompleted.map((p) => p._id === projectId ? project : p);
            setProjectsAsPmCompleted(newProjectsAsPmCompleted);
        }

        // employer
        else if (projectsAsEmployerDraft.find((p) => p._id === projectId)) {
            const newProjectsAsEmployerDraft = projectsAsEmployerDraft.map((p) => p._id === projectId ? project : p);
            setProjectsAsEmployerDraft(newProjectsAsEmployerDraft);
        } else if (projectsAsEmployerNotStarted.find((p) => p._id === projectId)) {
            const newProjectsAsEmployerNotStarted = projectsAsEmployerNotStarted.map((p) => p._id === projectId ? project : p);
            setProjectsAsEmployerNotStarted(newProjectsAsEmployerNotStarted);
        } else if (projectsAsEmployerWaitingForApproval.find((p) => p._id === projectId)) {
            const newProjectsAsEmployerWaitingForApproval = projectsAsEmployerWaitingForApproval.map((p) => p._id === projectId ? project : p);
            setProjectsAsEmployerWaitingForApproval(newProjectsAsEmployerWaitingForApproval);
        } else if (projectsAsEmployerOngoing.find((p) => p._id === projectId)) {
            const newProjectsAsEmployerOngoing = projectsAsEmployerOngoing.map((p) => p._id === projectId ? project : p);
            setProjectsAsEmployerOngoing(newProjectsAsEmployerOngoing);
        } else if (projectsAsEmployerWaitingForCompletion.find((p) => p._id === projectId)) {
            const newProjectsAsEmployerWaitingForCompletion = projectsAsEmployerWaitingForCompletion.map((p) => p._id === projectId ? project : p);
            setProjectsAsEmployerWaitingForCompletion(newProjectsAsEmployerWaitingForCompletion);
        } else if (projectsAsEmployerCompleted.find((p) => p._id === projectId)) {
            const newProjectsAsEmployerCompleted = projectsAsEmployerCompleted.map((p) => p._id === projectId ? project : p);
            setProjectsAsEmployerCompleted(newProjectsAsEmployerCompleted);
        }

        return project;
    }

    const createProjectDraft = async (project: IProject): Promise<IProject> => {
        const newProject = await _createProjectDraft(project);
        setProjectsAsEmployerDraft([newProject, ...projectsAsEmployerDraft]);
        return newProject;
    }

    const updateProjectDetails = async (project: IProject): Promise<IProject> => {
        const updatedProject = await _updateProjectDetails(project);
        // find project with id in projectsAsPmOngoing, projectsAsEmployerDraft, projectsAsEmployerNotStarted or projectsAsEmployerOngoing
        if (projectsAsPmOngoing.find((p) => p._id === updatedProject._id)) {
            const newProjectsAsPmOngoing = projectsAsPmOngoing.map((p) => p._id === updatedProject._id ? updatedProject : p);
            setProjectsAsPmOngoing([...newProjectsAsPmOngoing]);
        } else if (projectsAsEmployerDraft.find((p) => p._id === updatedProject._id)) {
            const newProjectsAsEmployerDraft = projectsAsEmployerDraft.map((p) => p._id === updatedProject._id ? updatedProject : p);
            setProjectsAsEmployerDraft(newProjectsAsEmployerDraft);
        } else if (projectsAsEmployerNotStarted.find((p) => p._id === updatedProject._id)) {
            const newProjectsAsEmployerNotStarted = projectsAsEmployerNotStarted.map((p) => p._id === updatedProject._id ? updatedProject : p);
            setProjectsAsEmployerDraft(newProjectsAsEmployerNotStarted);
        } else if (projectsAsEmployerOngoing.find((p) => p._id === updatedProject._id)) {
            const newProjectsAsEmployerOngoing = projectsAsEmployerOngoing.map((p) => p._id === updatedProject._id ? updatedProject : p);
            setProjectsAsEmployerDraft(newProjectsAsEmployerOngoing);
        }

        return updatedProject;
    }

    

    // called by employers - therefore only employer data must be updated
    const deleteProjectDraft = async (projectId: string) => {
        await _deleteProjectDraft(projectId);
        const newProjectsAsEmployerDraft = projectsAsEmployerDraft.filter((p) => p._id !== projectId);
        setProjectsAsEmployerDraft(newProjectsAsEmployerDraft);
    }

    // called by employers - therefore only employer data must be updated
    const publishProjectDraft = async (projectId: string): Promise<IProject> => {
        const project = await _publishProject(projectId);
        const newProjectsAsEmployerDraft = projectsAsEmployerDraft.filter((p) => p._id !== projectId);
        setProjectsAsEmployerDraft(sortByCreatedAt(newProjectsAsEmployerDraft));
        const newProjectsAsEmployerNotStarted = [project, ...projectsAsEmployerNotStarted];
        setProjectsAsEmployerNotStarted(sortByCreatedAt(newProjectsAsEmployerNotStarted));
        return project;
    }

    // called by employers - therefore only employer data must be updated
    const approveProjectDetails = async (projectId: string): Promise<IProject> => {
        const project = await _approveProjectDetails(projectId);
        const newProjectsAsEmployerNotStarted = projectsAsEmployerNotStarted.filter((p) => p._id !== projectId);
        setProjectsAsEmployerNotStarted(newProjectsAsEmployerNotStarted);
        const newProjectsAsEmployerWaitingForApproval = projectsAsEmployerWaitingForApproval.filter((p) => p._id !== projectId);
        setProjectsAsEmployerWaitingForApproval(newProjectsAsEmployerWaitingForApproval);
        const newProjectsAsEmployerOngoing = [project, ...projectsAsEmployerOngoing];
        setProjectsAsEmployerOngoing(sortByCreatedAt(newProjectsAsEmployerOngoing));
        return project;
    }

    // called by pms
    const requestProjectApproval = async (projectId: string): Promise<IProject> => {
        const project = await _requestProjectApproval(projectId);
        const newProjectsAsPmOngoing = projectsAsPmOngoing.filter((p) => p._id !== projectId);
        setProjectsAsPmOngoing(newProjectsAsPmOngoing);
        const newProjectsAsPmWaitingForApproval = [project, ...projectsAsPmWaitingForApproval.filter((p) => p._id !== projectId)];
        setProjectsAsPmWaitingForApproval(sortByCreatedAt(newProjectsAsPmWaitingForApproval));
        return project;
    }

    // called by employers
    const rejectApprovalRequest = async (projectId: string, reason?: string): Promise<IProject> => {
        const project = await _rejectApprovalRequest(projectId, reason);
        const newProjectsAsEmployerNotStarted = [project, ...projectsAsEmployerNotStarted];
        setProjectsAsEmployerNotStarted(sortByCreatedAt(newProjectsAsEmployerNotStarted));
        const newProjectsAsEmployerWaitingForApproval = projectsAsEmployerWaitingForApproval.filter((p) => p._id !== projectId);
        setProjectsAsEmployerWaitingForApproval(newProjectsAsEmployerWaitingForApproval);
        return project;
    }

    // called by pms
    const requestProjectCompletion = async (projectId: string): Promise<IProject> => {
        const project = await _requestProjectCompletion(projectId);
        const newProjectsAsPmWaitingForCompletion = [project, ...projectsAsPmWaitingForCompletion];
        setProjectsAsPmWaitingForCompletion(newProjectsAsPmWaitingForCompletion);
        const newProjectsAsPmOngoing = projectsAsPmOngoing.filter((p) => p._id !== projectId);
        setProjectsAsPmOngoing(newProjectsAsPmOngoing);
        return project;
    }

    // called by employers
    const rejectProjectCompletionRequest = async (projectId: string, reason: string): Promise<IProject> => {
        const project = await _rejectCompletionRequest(projectId, reason);
        const newProjectsAsEmployerWaitingForCompletion = [...projectsAsEmployerWaitingForCompletion].filter((p) => p._id !== projectId);
        setProjectsAsEmployerWaitingForCompletion(newProjectsAsEmployerWaitingForCompletion);
        const newProjectsAsEmployerOngoing = [project, ...projectsAsEmployerOngoing];
        setProjectsAsPmOngoing(sortByCreatedAt(newProjectsAsEmployerOngoing));
        return project;
    }

    // called by employers
    const completeProject = async (projectId: string): Promise<IProject> => {
        const project = await _completeProject(projectId);
        const newProjectsAsEmployerCompleted = [project, ...projectsAsEmployerCompleted];
        setProjectsAsEmployerCompleted(sortByCreatedAt(newProjectsAsEmployerCompleted));
        const newProjectsAsEmployerWaitingForCompletion = [...projectsAsEmployerWaitingForCompletion].filter((p) => p._id !== projectId);
        setProjectsAsEmployerWaitingForCompletion(newProjectsAsEmployerWaitingForCompletion);
        return project;
    }

    const getProjectById = (projectId: string) => {
        for (const project of [
            ...projectsAsFreelancerInProgress,
            ...projectsAsFreelancerCompleted,

            ...projectsAsPmWaitingForApproval,
            ...projectsAsPmOngoing,
            ...projectsAsPmWaitingForCompletion,
            ...projectsAsPmCompleted,

            ...projectsAsEmployerDraft,
            ...projectsAsEmployerNotStarted,
            ...projectsAsEmployerWaitingForApproval,
            ...projectsAsEmployerOngoing,
            ...projectsAsEmployerWaitingForCompletion,
            ...projectsAsEmployerCompleted,
        ]) {
            if (project._id === projectId) {
                return project;
            }
        }
        return null;
    }


    return (
        <MyProjectsContext.Provider value={{
            projectsAsFreelancerInProgress,
            projectsAsFreelancerCompleted,

            projectsAsPmWaitingForApproval,
            projectsAsPmOngoing,
            projectsAsPmWaitingForCompletion,
            projectsAsPmCompleted,

            projectsAsEmployerDraft,
            projectsAsEmployerNotStarted,
            projectsAsEmployerWaitingForApproval,
            projectsAsEmployerOngoing,
            projectsAsEmployerWaitingForCompletion,
            projectsAsEmployerCompleted,

            projectIdTeamMap,
            projectIdEmployerMap,
            projectIdPmMap,

            getProjectById,
            fetchProjectsAsFreelancer,
            fetchProjectsAsPm,
            fetchProjectsAsEmployer,
            refetchProjectById,

            createProjectDraft,
            updateProjectDetails,
            deleteProjectDraft,
            publishProjectDraft,
            requestProjectApproval,
            rejectApprovalRequest,
            approveProjectDetails,
            requestProjectCompletion,
            rejectProjectCompletionRequest,
            completeProject,
        }}>
            {children}
        </MyProjectsContext.Provider>
    );
};
